import { useState } from 'react'
import {
    File06,
    FileCheck02,
    Eye,
    XClose,
    Package,
    LinkExternal01,
} from '@untitledui/icons'
import useTranslation from '@/hooks/useTranslation'
import Button from '@/components/Button'
import ResponsiveDrawer from '@/components/ResponsiveDrawer'
import OrderStatusBadge from '@/components/OrderStatusBadge'

export default function OrderDetails({ order, formatDate, formatPrice }) {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    const amount = order.amounts?.total ?? 0

    return (
        <>
            <Button
                size="sm"
                variant="gray"
                icon={<Eye size={16} />}
                onClick={() => setIsOpen(true)}
            >
                {t('orders.detail')}
            </Button>

            <ResponsiveDrawer
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                size="sm"
            >
                <div className="flex items-center justify-between p-[24px] border-b border-dark-blue-10">
                    <div className="flex flex-col gap-[4px]">
                        <span className="text-[18px] font-semibold uppercase">
                            {t('orders.details')}
                        </span>

                        <span className="text-[14px] text-dark-blue-40">
                            {order.number}
                        </span>
                    </div>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="cursor-pointer"
                    >
                        <XClose size={20} />
                    </button>
                </div>

                <div className="flex flex-col px-[20px] py-[30px] gap-[8px]">
                    <div className="flex flex-col gap-[10px] w-full">
                        <div className="flex flex-row flex-wrap gap-[8px]">
                            <div className="flex flex-col gap-[12px] p-[20px] bg-[#F5F5F6] rounded-[8px] flex-1 min-w-0">
                                <span className="text-[14px] font-semibold text-dark-blue/40">
                                    {t('orders.date')}
                                </span>
                                <span className="text-[14px]">
                                    {formatDate(order.date)}
                                </span>
                            </div>
                            <div className="flex flex-col gap-[12px] p-[20px] bg-[#F5F5F6] rounded-[8px] flex-1 min-w-0">
                                <span className="text-[14px] font-semibold text-dark-blue/40">
                                    {t('orders.priceHT')}
                                </span>
                                <span className="text-[14px]">
                                    {formatPrice(amount)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {order.shipments?.length > 0 && (
                        <div className="flex flex-col gap-[8px] w-full">
                            {order.shipments.map((shipment) => (
                                <div
                                    key={shipment.tracking_code}
                                    className="flex flex-col gap-[12px] p-[20px] bg-[#F5F5F6] rounded-[8px]"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-[8px]">
                                            <Package size={16} />
                                            <OrderStatusBadge
                                                type="shipment"
                                                status={
                                                    shipment.tracking_status
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-row flex-wrap gap-[8px]">
                                        {shipment.shipped_at && (
                                            <div className="flex flex-col gap-[4px] flex-1 min-w-0">
                                                <span className="text-[12px] font-semibold text-dark-blue/40">
                                                    {t('orders.shippedOn')}
                                                </span>
                                                <span className="text-[13px]">
                                                    {formatDate(
                                                        shipment.shipped_at
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {shipment.delivered_at ? (
                                            <div className="flex flex-col gap-[4px] flex-1 min-w-0">
                                                <span className="text-[12px] font-semibold text-dark-blue/40">
                                                    {t('orders.deliveredOn')}
                                                </span>
                                                <span className="text-[13px]">
                                                    {formatDate(
                                                        shipment.delivered_at
                                                    )}
                                                </span>
                                            </div>
                                        ) : shipment.est_delivery_date ? (
                                            <div className="flex flex-col gap-[4px] flex-1 min-w-0">
                                                <span className="text-[12px] font-semibold text-dark-blue/40">
                                                    {t(
                                                        'orders.estimatedDelivery'
                                                    )}
                                                </span>
                                                <span className="text-[13px]">
                                                    {formatDate(
                                                        shipment.est_delivery_date
                                                    )}
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                    <a
                                        href={`https://www.dhl.com/en/express/tracking.html?AWB=${shipment.tracking_code}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-[8px] h-[36px] bg-white hover:bg-dark-blue/5 transition-colors rounded-[6px] text-[13px] font-semibold"
                                    >
                                        {t('orders.trackOnDhl')}
                                        <LinkExternal01 size={12} />
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex flex-col gap-[8px] w-full">
                        {order.pdf_link && (
                            <a
                                href={order.pdf_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-[8px] h-[40px] px-[12px] bg-[#F5F5F6] hover:bg-[#EBEBED] transition-colors rounded-[8px] text-[14px] font-mono font-medium"
                            >
                                <File06 size={14} />
                                {t('orders.details')} {order.number}
                            </a>
                        )}

                        {order.invoices?.map((inv) => (
                            <a
                                key={inv.number}
                                href={inv.pdf_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-[8px] h-[40px] px-[12px] bg-[#F5F5F6] hover:bg-[#EBEBED] transition-colors rounded-[8px] text-[14px] font-mono font-medium"
                            >
                                <FileCheck02 size={14} />
                                {t('orders.invoice')} {inv.number}
                            </a>
                        ))}
                    </div>
                </div>
            </ResponsiveDrawer>
        </>
    )
}
